import type { ExportNotesMessage, ExportNotesSection } from '../schemas/export-notes.js';
import type { ExportDressingOptions, ExportPageOptions } from '../schemas/export.js';
import { documentFooterEnabled } from './document-header.js';
import { AVG_CHAR_WIDTH_FACTOR, estimateWrappedLineCount } from './estimate-row-heights.js';
import {
  CSS_PX_PER_PT,
  FOOTER_RESERVED_PX,
  LINE_HEIGHT_FACTOR,
  type PageGeometry,
  pageGeometry,
} from './page-geometry.js';
import { escapeHtml, formatPx } from './render-export-html-parts.js';

/**
 * The « Notes » page of a document export (issue #19): the memo, then each open
 * Client discussion - its target in bold, « Auteur, date - texte », the replies
 * indented under it.
 *
 * The page is a fixed box like every other page of the paged document, so a
 * long section has to be cut into pages before it is drawn rather than left to
 * overflow. The cut is an estimate (`planNotesPages`) that the renderer and the
 * plan run through the same function: the PDF's page count, the footer's « Page
 * x sur N » and the preview's plan all agree on it.
 */

export const NOTES_TITLE = 'Notes';
export const NOTES_CONTINUED_TITLE = 'Notes (suite)';
const MEMO_HEADING = 'Mémo';

// ── Stylesheet metrics ───────────────────────────────────────────────────────
// Every vertical gap the stylesheet below draws is a number here, because the
// page budget (`planNotesPages`) must add up exactly what the stylesheet pins.

const TITLE_FONT_PX = 16;
/** The title's line box (`line-height: 1.3`, rounded up) plus its bottom margin. */
const TITLE_BLOCK_PX = Math.ceil(TITLE_FONT_PX * LINE_HEIGHT_FACTOR) + 10;
/** The air under every block. */
const BLOCK_GAP_PX = 6;
/** A discussion's head carries a rule above it: 1px of border and 8px of padding. */
const THREAD_HEAD_EXTRA_PX = 9;
const REPLY_INDENT_PX = 24;
/** Bold glyphs run wider than the regular ones the width estimate is calibrated on. */
const HEADING_WIDTH_FACTOR = 1.1;

/** One printed block: an optional bold heading and a paragraph, whole on a page. */
export interface ExportNotesBlock {
  /**
   * `memo`: the memo's first block. `thread`: a discussion's head. `reply`: a
   * reply's first block. `continued`: the rest of a text too long for a page.
   */
  kind: 'memo' | 'thread' | 'reply' | 'continued';
  heading: string | null;
  text: string;
  indented: boolean;
}

interface BlockMetrics {
  contentWidthPx: number;
  fontPx: number;
  /** The most lines of text a single block may hold - longer texts are cut. */
  maxTextLines: number;
}

function textLineCount(text: string, widthPx: number, fontPx: number): number {
  return text
    .split('\n')
    .reduce(
      (total, paragraph) => total + estimateWrappedLineCount(paragraph, false, widthPx, 0, fontPx),
      0,
    );
}

/**
 * Cuts `text` into pieces of at most `maxLines` estimated lines: at paragraph
 * boundaries, and inside a paragraph at its last space when one alone would not
 * fit. A text that fits comes back whole.
 */
function chunkText(text: string, widthPx: number, fontPx: number, maxLines: number): string[] {
  const charsPerLine = Math.max(8, Math.floor(widthPx / (fontPx * AVG_CHAR_WIDTH_FACTOR)));
  const capacity = charsPerLine * maxLines;
  const chunks: string[] = [];
  let current: string[] = [];
  let currentLines = 0;
  const flush = (): void => {
    if (current.length === 0) return;
    chunks.push(current.join('\n'));
    current = [];
    currentLines = 0;
  };

  for (const paragraph of text.split('\n')) {
    let rest = paragraph;
    while (textLineCount(rest, widthPx, fontPx) > maxLines) {
      const space = rest.lastIndexOf(' ', capacity);
      const cut = space > 0 ? space : capacity;
      flush();
      chunks.push(rest.slice(0, cut));
      rest = rest.slice(cut).trimStart();
    }
    const lines = textLineCount(rest, widthPx, fontPx);
    if (currentLines + lines > maxLines) flush();
    current.push(rest);
    currentLines += lines;
  }
  flush();
  return chunks;
}

function messageText(message: ExportNotesMessage): string {
  return `${message.author}, ${message.date} - ${message.text}`;
}

/** Turns the section into the blocks a page is made of, cutting any text longer than `maxTextLines`. */
export function buildNotesBlocks(
  section: ExportNotesSection,
  metrics: BlockMetrics,
): ExportNotesBlock[] {
  const { contentWidthPx, fontPx, maxTextLines } = metrics;
  const blocks: ExportNotesBlock[] = [];

  const push = (
    text: string,
    first: Pick<ExportNotesBlock, 'kind' | 'heading'>,
    indented: boolean,
  ): void => {
    const widthPx = indented ? contentWidthPx - REPLY_INDENT_PX : contentWidthPx;
    // No page to fit (the continuous document): the text goes whole.
    const chunks = Number.isFinite(maxTextLines)
      ? chunkText(text, widthPx, fontPx, maxTextLines)
      : [text];
    chunks.forEach((chunk, index) => {
      blocks.push(
        index === 0
          ? { ...first, text: chunk, indented }
          : { kind: 'continued', heading: null, text: chunk, indented },
      );
    });
  };

  if (section.memo !== null) push(section.memo, { kind: 'memo', heading: MEMO_HEADING }, false);
  for (const discussion of section.discussions) {
    push(messageText(discussion.message), { kind: 'thread', heading: discussion.target }, false);
    for (const reply of discussion.replies) {
      push(messageText(reply), { kind: 'reply', heading: null }, true);
    }
  }
  return blocks;
}

function blockHeightPx(block: ExportNotesBlock, metrics: BlockMetrics): number {
  const linePx = Math.ceil(metrics.fontPx * LINE_HEIGHT_FACTOR);
  const widthPx = block.indented
    ? metrics.contentWidthPx - REPLY_INDENT_PX
    : metrics.contentWidthPx;
  const headingLines =
    block.heading === null
      ? 0
      : estimateWrappedLineCount(
          block.heading,
          false,
          widthPx,
          0,
          metrics.fontPx * HEADING_WIDTH_FACTOR,
        );
  const textLines = textLineCount(block.text, widthPx, metrics.fontPx);
  return (
    (headingLines + textLines) * linePx +
    (block.kind === 'thread' ? THREAD_HEAD_EXTRA_PX : 0) +
    BLOCK_GAP_PX
  );
}

export interface PlanNotesPagesInput {
  section: ExportNotesSection;
  geometry: PageGeometry;
  /** The document's base font size - the notes are set in it. */
  fontSizePt: number;
  /** Whether the document draws a footer, which keeps `FOOTER_RESERVED_PX` clear at the bottom of every page. */
  footerEnabled: boolean;
}

/**
 * The notes pages: the blocks packed greedily into the page box, each page
 * keeping room for its title and the footer. A block never splits (texts that
 * would not fit a page were already cut in `buildNotesBlocks`).
 */
export function planNotesPages(input: PlanNotesPagesInput): ExportNotesBlock[][] {
  const { geometry } = input;
  const fontPx = input.fontSizePt * CSS_PX_PER_PT;
  const contentWidthPx = geometry.pageWidthPx - 2 * geometry.marginPx;
  const usableHeightPx =
    geometry.pageHeightPx -
    2 * geometry.marginPx -
    (input.footerEnabled ? FOOTER_RESERVED_PX : 0) -
    TITLE_BLOCK_PX;
  const linePx = Math.ceil(fontPx * LINE_HEIGHT_FACTOR);
  // A heading line and a rule's padding come on top of the text of the biggest block.
  const maxTextLines = Math.max(1, Math.floor(usableHeightPx / linePx) - 3);
  const metrics: BlockMetrics = { contentWidthPx, fontPx, maxTextLines };

  const pages: ExportNotesBlock[][] = [];
  let page: ExportNotesBlock[] = [];
  let usedPx = 0;
  for (const block of buildNotesBlocks(input.section, metrics)) {
    const heightPx = blockHeightPx(block, metrics);
    if (page.length > 0 && usedPx + heightPx > usableHeightPx) {
      pages.push(page);
      page = [];
      usedPx = 0;
    }
    page.push(block);
    usedPx += heightPx;
  }
  if (page.length > 0) pages.push(page);
  return pages;
}

/**
 * How many pages the notes add to a PDF export of this page setup - the number
 * the server hands back in the render facts and the browser's plan counts.
 */
export function notesPageCount(
  section: ExportNotesSection | null,
  page: Pick<ExportPageOptions, 'format' | 'orientation' | 'margins' | 'fontSizePt'>,
  dressing: Pick<
    ExportDressingOptions,
    'footerText' | 'pageNumbers' | 'pmsSignature' | 'assemblyMarks'
  >,
): number {
  if (section === null) return 0;
  return planNotesPages({
    section,
    geometry: pageGeometry(page),
    fontSizePt: page.fontSizePt,
    footerEnabled: documentFooterEnabled(dressing),
  }).length;
}

// ── Markup ───────────────────────────────────────────────────────────────────

/** The page's title: « Notes », or « Notes (suite) » on the pages after the first. */
export function renderNotesTitleHtml(continued: boolean): string {
  return `<h2 class="export-notes-title">${continued ? NOTES_CONTINUED_TITLE : NOTES_TITLE}</h2>`;
}

export function renderNotesBlocksHtml(blocks: readonly ExportNotesBlock[]): string {
  return blocks
    .map((block) => {
      const heading =
        block.heading === null
          ? ''
          : `<span class="export-notes-heading">${escapeHtml(block.heading)}</span>`;
      const indent = block.indented ? ' export-notes-indented' : '';
      return `<div class="export-notes-block export-notes-${block.kind}${indent}">${heading}<p class="export-notes-text">${escapeHtml(block.text)}</p></div>`;
    })
    .join('');
}

/** The continuous HTML document's notes: one section at the end, no page to fit. */
export function renderNotesSectionHtml(section: ExportNotesSection): string {
  const blocks = buildNotesBlocks(section, {
    contentWidthPx: 0,
    fontPx: 0,
    maxTextLines: Number.POSITIVE_INFINITY,
  });
  return `<section class="export-notes">${renderNotesTitleHtml(false)}${renderNotesBlocksHtml(blocks)}</section>`;
}

/** The notes' stylesheet - emitted only when the document carries a notes section. */
export function notesStyle(fontSizePt: number): string {
  const fontPx = formatPx(fontSizePt * CSS_PX_PER_PT);
  return `
  .export-notes-title { margin: 0 0 ${TITLE_BLOCK_PX - Math.ceil(TITLE_FONT_PX * LINE_HEIGHT_FACTOR)}px; font-size: ${TITLE_FONT_PX}px; line-height: ${LINE_HEIGHT_FACTOR}; font-weight: 700; color: #312783; }
  .export-notes-block { margin: 0 0 ${BLOCK_GAP_PX}px; font-size: ${fontPx}px; line-height: ${LINE_HEIGHT_FACTOR}; }
  .export-notes-thread { border-top: 1px solid #d9d6d2; padding-top: 8px; }
  .export-notes-heading { display: block; font-weight: 700; }
  .export-notes-text { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; }
  .export-notes-indented { margin-left: ${REPLY_INDENT_PX}px; color: #6b6a67; }
  .export-notes { margin-top: 24px; max-width: 960px; }
`.replace(/\s+/g, ' ');
}
